import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, LinearProgress, Paper, Typography } from '@mui/material';
import { PassengerDocumentEnum } from 'features/salesOrder/types';
import {
	LoadedQuotation,
	PassengerSlot,
	QuotationImportPersistedState,
	WizardSavedPassenger,
	buildPassengerSlots,
	getRoomBaseConfigFromQuotation,
} from '../../types';
import { getSlotLabelById, passengerSlotKindMeta } from '../../helpers/passengerSlotDisplay';
import { PassengerDocumentLookup } from '../passengers/PassengerDocumentLookup';
import { PassengerCreateForm } from '../passengers/PassengerCreateForm';
import { PassengerSlotHeader } from '../passengers/PassengerSlotHeader';
import { saveQuotationImportState } from '../../sessionStorage';

type PassengerSubStep = 'lookup' | 'form';

interface Props {
	saleOrderId: string;
	quotation: LoadedQuotation;
	initialState?: QuotationImportPersistedState | null;
	onComplete: (saved: WizardSavedPassenger[]) => void;
}

export const StepPassengers = ({ saleOrderId, quotation, initialState, onComplete }: Props): JSX.Element => {
	const slots = useMemo(() => buildPassengerSlots(getRoomBaseConfigFromQuotation(quotation)), [quotation]);

	const [slotIndex, setSlotIndex] = useState(initialState?.currentPassengerSlotIndex ?? 0);
	const [savedPassengers, setSavedPassengers] = useState<WizardSavedPassenger[]>(initialState?.savedPassengers ?? []);
	const [subStep, setSubStep] = useState<PassengerSubStep>('lookup');
	const [documentType, setDocumentType] = useState<PassengerDocumentEnum>(PassengerDocumentEnum.CEDULA);
	const [documentNumber, setDocumentNumber] = useState('');
	const [existingPassengerId, setExistingPassengerId] = useState<string | undefined>();

	const currentSlot: PassengerSlot | undefined = slots[slotIndex];
	const progress = slots.length > 0 ? (savedPassengers.length / slots.length) * 100 : 0;

	const persist = (nextSaved: WizardSavedPassenger[], nextIndex: number) => {
		saveQuotationImportState({
			quotationId: quotation.id,
			saleOrderId,
			step: 'passengers',
			savedPassengers: nextSaved,
			currentPassengerSlotIndex: nextIndex,
		});
	};

	const advanceAfterSave = (entry: WizardSavedPassenger) => {
		const nextSaved = [...savedPassengers, entry];
		setSavedPassengers(nextSaved);

		if (slotIndex >= slots.length - 1) {
			persist(nextSaved, slotIndex);
			onComplete(nextSaved);
			return;
		}

		const nextIndex = slotIndex + 1;
		setSlotIndex(nextIndex);
		setSubStep('lookup');
		setDocumentNumber('');
		setExistingPassengerId(undefined);
		persist(nextSaved, nextIndex);
	};

	if (slots.length === 0) {
		return (
			<Box>
				<Alert severity="info" sx={{ mb: 2 }}>
					No hay pasajeros definidos en la cotización. Podés continuar y cargarlos en la pantalla de la orden.
				</Alert>
				<Box display="flex" justifyContent="flex-end">
					<Button variant="contained" onClick={() => onComplete([])}>
						Continuar
					</Button>
				</Box>
			</Box>
		);
	}

	if (!currentSlot) {
		return null;
	}

	return (
		<Box>
			<Typography variant="h6" fontWeight={700} gutterBottom>
				Pasajeros según cotización
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
				Orden de carga: {slots.map((s) => s.label).join(' → ')}
			</Typography>
			<LinearProgress variant="determinate" value={progress} sx={{ mb: 3, height: 8, borderRadius: 1 }} />

			{savedPassengers.length > 0 && (
				<Box display="flex" flexWrap="wrap" gap={1} mb={3}>
					{savedPassengers.map((p) => {
						const kind = p.slotKind ?? slots.find((s) => s.id === p.slotId)?.kind ?? 'adult';
						const label = p.slotLabel ?? getSlotLabelById(slots, p.slotId) ?? passengerSlotKindMeta[kind].chipLabel;
						const meta = passengerSlotKindMeta[kind];
						return (
							<Chip
								key={p.keyId}
								label={`${meta.chipLabel}: ${p.name} ${p.lastName}`}
								title={label}
								color="success"
								size="small"
								variant="outlined"
							/>
						);
					})}
				</Box>
			)}

			<Paper variant="outlined" sx={{ p: 3 }}>
				<PassengerSlotHeader
					slot={currentSlot}
					slotIndex={slotIndex}
					totalSlots={slots.length}
					subtitle={
						subStep === 'lookup'
							? 'Ingresá el documento de esta persona (según el tipo indicado arriba).'
							: existingPassengerId
							? 'Confirmá los datos de esta persona.'
							: 'Completá los datos de esta persona.'
					}
				/>

				{subStep === 'lookup' && (
					<PassengerDocumentLookup
						onExistingSelected={(passengerId) => {
							setExistingPassengerId(passengerId);
							setSubStep('form');
						}}
						onNotFound={(docType, docNumber) => {
							setDocumentType(docType);
							setDocumentNumber(docNumber);
							setExistingPassengerId(undefined);
							setSubStep('form');
						}}
					/>
				)}

				{subStep === 'form' && (
					<PassengerCreateForm
						saleOrderId={saleOrderId}
						slot={currentSlot}
						documentType={documentType}
						documentNumber={documentNumber}
						isMainPassenger={slotIndex === 0}
						existingPassengerId={existingPassengerId}
						onSaved={(data) =>
							advanceAfterSave({
								keyId: data.keyId,
								name: data.name,
								lastName: data.lastName,
								passengerId: data.passengerId,
								slotId: currentSlot.id,
								slotLabel: currentSlot.label,
								slotKind: currentSlot.kind,
							})
						}
						onCancel={() => {
							setSubStep('lookup');
							setExistingPassengerId(undefined);
						}}
					/>
				)}
			</Paper>
		</Box>
	);
};
